import React, { useRef, useState } from 'react';
import {
  Filter as FilterAdmin,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  TextInput,
  useListContext,
} from 'react-admin';
import { Form, FormSpy } from 'react-final-form';

import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';

import { PeriodoRapidoInput } from '../../Components/InputsReactAdmin';
import { STATUS_ATIVO } from '../../utils/constants';
import { nomeFornecedor } from './FornecedorField';

const useStyles = makeStyles((theme) => ({
  barra: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    padding: '10px 12px 2px',
    marginTop: 12,
    marginBottom: 8,
    boxShadow: theme.shadows[1],
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '0 12px',
    '& form': {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
      gap: '0 12px',
      flex: '1 1 0',
      minWidth: 0,
      margin: 0,
      padding: 0,
      minHeight: 0,
    },
    // espaçador que o react-admin coloca depois de cada filtro
    '& .filter-field > [class*="spacer"]': { display: 'none' },
    '& .filter-field': { alignItems: 'flex-start', marginBottom: 8 },
    '& .MuiFormControl-root': { margin: 0 },
    // fornecedor e chave dividem o espaço que sobra, para a barra ocupar a largura da tabela
    '& [data-source="fornecedor_id"]': { flex: '1 1 180px', minWidth: 150 },
    '& [data-source="chave_acesso_nota_fiscal"]': {
      flex: '1 1 170px',
      minWidth: 150,
    },
    '& .filter-field > .MuiFormControl-root, & .filter-field > .MuiAutocomplete-root': {
      width: '100%',
    },
  },
}));

// filtros que ficam no painel lateral (os demais ficam na barra)
const CHAVES_PAINEL = ['periodo', 'usado_em_compra', 'pago'];

const vazio = (v) => v === undefined || v === null || v === '';

const dois = (n) => String(n).padStart(2, '0');
const mostrarData = (d) =>
  `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()}`;

function textoPeriodo(periodo) {
  if (!periodo || !periodo.start || !periodo.end) return '';
  const ini = mostrarData(new Date(periodo.start));
  const fim = mostrarData(new Date(periodo.end));
  return ini === fim ? ini : `${ini} - ${fim}`;
}

const useStylesPainel = makeStyles((theme) => ({
  painel: { width: 340, maxWidth: '100vw' },
  cabecalho: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  corpo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const PainelFiltros = ({ open, onClose }) => {
  const classes = useStylesPainel();
  const { filterValues, setFilters } = useListContext();
  const atual = useRef(filterValues);
  atual.current = filterValues;

  // aplica na hora: copia os 3 filtros do painel para os filtros da lista
  function aplicar(values) {
    const proximo = { ...atual.current };
    CHAVES_PAINEL.forEach((k) => {
      if (vazio(values[k])) delete proximo[k];
      else proximo[k] = values[k];
    });
    const mudou = CHAVES_PAINEL.some(
      (k) => JSON.stringify(proximo[k]) !== JSON.stringify(atual.current[k]),
    );
    if (mudou) setFilters(proximo);
  }

  const inicial = {};
  CHAVES_PAINEL.forEach((k) => {
    if (!vazio(filterValues[k])) inicial[k] = filterValues[k];
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className={classes.painel}>
        <div className={classes.cabecalho}>
          <Typography variant="h6">Filtros</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </div>
        <Form
          onSubmit={() => {}}
          initialValues={inicial}
          render={({ form }) => (
            <div className={classes.corpo}>
              <FormSpy
                subscription={{ values: true }}
                onChange={({ values }) => aplicar(values)}
              />
              <PeriodoRapidoInput source="periodo" label="Período" />
              <SelectInput
                fullWidth
                variant="outlined"
                margin="dense"
                resource="descarrego_mercadorias"
                label="Compra"
                source="usado_em_compra"
                allowEmpty
                emptyText="Todos"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                choices={[
                  { id: true, name: 'Usado' },
                  { id: false, name: 'Não usado' },
                ]}
              />
              <SelectInput
                fullWidth
                variant="outlined"
                margin="dense"
                resource="descarrego_mercadorias"
                label="Pagamento"
                source="pago"
                allowEmpty
                emptyText="Todos"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
                choices={[
                  { id: true, name: 'Pago' },
                  { id: false, name: 'Pendente' },
                ]}
              />
              <Box display="flex" justifyContent="space-between">
                <Button
                  onClick={() =>
                    form.batch(() =>
                      CHAVES_PAINEL.forEach((k) => form.change(k, undefined)),
                    )
                  }
                >
                  Limpar
                </Button>
                <Button color="primary" variant="contained" onClick={onClose}>
                  Fechar
                </Button>
              </Box>
            </div>
          )}
        />
      </div>
    </Drawer>
  );
};

// chips dos filtros do painel que estão ativos
const ChipsFiltros = () => {
  const { filterValues, setFilters } = useListContext();

  const chips = [];
  const periodo = textoPeriodo(filterValues.periodo);
  if (periodo) chips.push({ chave: 'periodo', texto: `Período: ${periodo}` });
  if (!vazio(filterValues.usado_em_compra)) {
    const usado =
      filterValues.usado_em_compra === true ||
      filterValues.usado_em_compra === 'true';
    chips.push({
      chave: 'usado_em_compra',
      texto: `Compra: ${usado ? 'Usado' : 'Não usado'}`,
    });
  }
  if (!vazio(filterValues.pago)) {
    const pago = filterValues.pago === true || filterValues.pago === 'true';
    chips.push({
      chave: 'pago',
      texto: `Pagamento: ${pago ? 'Pago' : 'Pendente'}`,
    });
  }
  if (chips.length === 0) return null;

  function remover(chave) {
    const { [chave]: removido, ...resto } = filterValues;
    setFilters(resto);
  }

  return (
    <Box display="flex" flexWrap="wrap" width="100%" mb={1} style={{ gap: 8 }}>
      {chips.map((c) => (
        <Chip
          key={c.chave}
          color="primary"
          variant="outlined"
          label={c.texto}
          onDelete={() => remover(c.chave)}
        />
      ))}
    </Box>
  );
};

// onNovo (opcional): mostra o botão de novo descarrego ao lado do botão Filtros
const Filter = ({ onNovo, ...props }) => {
  const classes = useStyles();
  const [painelAberto, setPainelAberto] = useState(false);
  const { filterValues } = useListContext();
  const qtdAtivos = CHAVES_PAINEL.filter((k) => !vazio(filterValues[k])).length;

  // na barra de ações o react-admin renderiza o botão de filtros: sem moldura
  const Moldura = props.context === 'button' ? React.Fragment : 'div';
  const propsMoldura =
    props.context === 'button' ? {} : { className: classes.barra };

  return (
    <Moldura {...propsMoldura}>
      <FilterAdmin {...props} variant="outlined" margin="dense">
        <ReferenceInput
          fullWidth
          label="Fornecedor"
          source="fornecedor_id"
          reference="fornecedores"
          sort={{ field: 'nome', order: 'ASC' }}
          filterToQuery={(searchText) => ({ busca: `${searchText}` })}
          filter={{ status: STATUS_ATIVO, ids_excluso: [1, 2] }}
          allowEmpty
          alwaysOn
        >
          <AutocompleteInput
            resettable
            optionText={(fornecedor) =>
              fornecedor && fornecedor.id ? nomeFornecedor(fornecedor) : ''
            }
          />
        </ReferenceInput>
        <TextInput
          fullWidth
          source="chave_acesso_nota_fiscal"
          label="Chave ou nº da NF-e"
          alwaysOn
        />
      </FilterAdmin>
      {props.context !== 'button' && (
        <>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterListIcon />}
            style={{ height: 40, marginBottom: 8 }}
            onClick={() => setPainelAberto(true)}
          >
            Filtros{qtdAtivos > 0 ? ` (${qtdAtivos})` : ''}
          </Button>
          {onNovo && (
            <Tooltip title="Novo descarrego">
              <Button
                variant="contained"
                color="primary"
                style={{
                  height: 40,
                  minWidth: 40,
                  padding: 0,
                  marginBottom: 8,
                }}
                onClick={onNovo}
              >
                <AddIcon />
              </Button>
            </Tooltip>
          )}
          <ChipsFiltros />
          <PainelFiltros
            open={painelAberto}
            onClose={() => setPainelAberto(false)}
          />
        </>
      )}
    </Moldura>
  );
};

export default Filter;
